import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Button, Select, InputLabel, MenuItem, FormControl, Modal, Box, Tooltip, AppBar, Toolbar, Paper, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function EditNationality() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [countryData, setCountryData] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [nationality, setNationality] = useState('');
  const { nationalityId } = useParams();
  let navigate = useNavigate();

  // Create a mapping between nationality names and their typical country IDs
  // You'll need to adjust these based on your actual country IDs from the country API
  const nationalityCountryMapping = {
    "Indian": "India",
    "American": "United States", // or "USA"
    "Australians": "Australia",
    "British": "United Kingdom",
    "Canadian": "Canada",
    "German": "Germany",
    "French": "France",
    "Japanese": "Japan",
    "Chinese": "China",
    // Add more mappings as needed
  };

  useEffect(() => {
    fetchCountryData();
    fetchNationalityData();
  }, []);

  const fetchCountryData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/Country/GetCountry`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        console.log('Country Data:', responseData);
        setCountryData(responseData);
      } else {
        console.error('Error fetching country data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const fetchNationalityData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/Nationality/GetNationality`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (response.ok) {
        const nationalityData = await response.json();
        const foundNationality = nationalityData.find(item => item.nationalityId === parseInt(nationalityId));
        if (foundNationality) {
          setNationality(foundNationality.nationality);

          // If the API returns a countryId for this nationality, use it directly
          if (foundNationality.countryId) {
            setSelectedCountry(foundNationality.countryId);
          } else {
            // Otherwise try to map nationality to country name using mapping
            const expectedCountryName = nationalityCountryMapping[foundNationality.nationality];
            if (expectedCountryName && countryData.length > 0) {
              const matchingCountry = countryData.find(country =>
                country.countryName.toLowerCase().includes(expectedCountryName.toLowerCase()) ||
                expectedCountryName.toLowerCase().includes(country.countryName.toLowerCase())
              );
              if (matchingCountry) {
                setSelectedCountry(matchingCountry.countryId);
              }
            }
          }
        } else {
          console.error('Nationality not found');
        }
      } else {
        console.error('Error fetching nationality data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  // Re-run nationality mapping when country data is loaded
  useEffect(() => {
    if (countryData.length > 0 && nationality && !selectedCountry) {
      const expectedCountryName = nationalityCountryMapping[nationality];
      if (expectedCountryName) {
        const matchingCountry = countryData.find(country =>
          country.countryName.toLowerCase().includes(expectedCountryName.toLowerCase()) ||
          expectedCountryName.toLowerCase().includes(country.countryName.toLowerCase())
        );
        if (matchingCountry) {
          setSelectedCountry(matchingCountry.countryId);
        }
      }
    }
  }, [countryData, nationality, selectedCountry]);

  const handleUpdate = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const apiUrl = `${process.env.REACT_APP_BASE_URL}/Nationality/Id`;

      // Adjust this payload based on what your API actually expects
      const payload = {
        nationalityId: nationalityId,
        nationality: nationality,
        countryId: selectedCountry // Include countryId if required
      };

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowSuccessModal(true);
      } else {
        console.error('Failed to update nationality');
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  return (
    <Container mt={4}>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Edit Nationality
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} sx={{ padding: 2, width: '100%', margin: 'auto', marginTop: 1 }}>
        <FormControl fullWidth sx={{ marginBottom: 3 }}>
          <InputLabel id="countryName-label">Country Name</InputLabel>
          <Select
            labelId="countryName-label"
            id="countryName"
            value={selectedCountry}
            label="Country Name"
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            <MenuItem value="" disabled>Select Country</MenuItem>
            {countryData.map((country) => (
              <MenuItem key={country.countryId} value={country.countryId}>
                {country.countryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Nationality"
          id="nationality"
          sx={{ marginBottom: 3 }}
          value={nationality}
          onChange={(e) => setNationality(e.target.value)}
        />
        <Tooltip title={!selectedCountry && "Select a Country" || !nationality && "Enter Nationality"} arrow>
          <span>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedCountry || !nationality}
              onClick={handleUpdate}
              sx={{ marginRight: 2 }}
            >
              UPDATE
            </Button>
          </span>
        </Tooltip>
        <Button variant="contained" color="error" onClick={() => { navigate(-1) }}>
          CANCEL
        </Button>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Nationality Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Nationality Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </Container>
  );
}

export default EditNationality;